import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { initSentry } from "@/lib/sentry";
import { installPreviewMock } from "@/v2/previewMock";

// ─── LOCAL PREVIEW MODE (no backend) ────────────────────────────────────────
// TravelTrucks preview: on localhost with no real backend, (1) seed an admin
// token so the OS opens straight to the board with no login page, and
// (2) install demo data so the Desk renders populated instead of "Couldn't
// load the Desk". Both are guarded to localhost + a missing/dead backend URL,
// so the moment REACT_APP_BACKEND_URL points at a real API this is all off.
// REMOVE before shipping to real users.
(() => {
  const h = window.location.hostname;
  const isLocal = h === 'localhost' || h === '127.0.0.1' || h === '' || h === '[::1]';
  const be = process.env.REACT_APP_BACKEND_URL || '';
  const noRealBackend = be === '' || /:9999(\/|$)/.test(be) || process.env.REACT_APP_PREVIEW_MOCK === '1';
  if (isLocal && noRealBackend) {
    if (!localStorage.getItem('kindra-admin-token')) {
      localStorage.setItem('kindra-admin-token', 'dev-preview');
    }
    installPreviewMock(); // patch fetch BEFORE React renders / any tile fetches
  }
})();

initSentry();

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // updateViaCache: 'none' — the SW script itself is always fetched fresh,
    // so a new deploy is picked up on the very next page load.
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
      .then((reg) => {
        // Poll for a new SW every 60s while the tab is open.
        setInterval(() => { reg.update().catch(() => {}); }, 60_000);
      })
      .catch(() => {});
    // When the SW tells us it took over, reload once so the new bundle runs.
    let reloaded = false;
    navigator.serviceWorker.addEventListener('message', (e) => {
      if (e.data && e.data.type === 'SW_UPDATED' && !reloaded) {
        reloaded = true;
        window.location.reload();
      }
    });
  });
}

// Build stamp — surface a version tag anywhere in the UI so MJ can eyeball
// which deploy he's on without needing DevTools.
window.__KINDRA_BUILD__ = process.env.REACT_APP_BUILD_ID || 'dev';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
